import React from 'react';
import { CustomField, CustomFieldEntityType, CustomFieldType, CustomFieldValue } from '../../../../types/custom_field';
import { BaseFilter, BaseFilterProps } from '../common/BaseFilter';
import { FilterProps } from '../common/filter.util';
import { CustomFieldFilterDropdownContent } from './DropdownContent';
import { itemsContainText, toFilterString } from '../../../../util/string.util';
import { asTranslatedString } from '../../../../types/translation/Translator';
import { WithCustomFields } from '../../../../types/custom_field/customField';

export class CustomFieldFilter extends BaseFilter<CustomField> {
  customField: CustomField;
  values: CustomFieldValue[];

  constructor(customField: CustomField, values: Map<string, WithCustomFields>) {
    super();
    this.customField = customField;
    this.values = [...values.values()]
      .map(v =>
        v
          .customFieldValues()
          .filter(cf => cf.id === customField.id)
          .map(cf => cf),
      )
      .flat();
  }

  static for(
    entityType: CustomFieldEntityType,
    values: Map<string, WithCustomFields>,
    customFields: Map<string, CustomField>,
  ) {
    return [...customFields.values()]
      .filter(cf => cf.type !== CustomFieldType.file && cf.entityType === entityType)
      .map(cf => new CustomFieldFilter(cf, values));
  }

  static filter(
    filterProps: FilterProps,
    item: WithCustomFields = new WithCustomFields([]),
    customFields: Map<string, CustomField> = new Map(),
    type: CustomFieldEntityType = CustomFieldEntityType.productMasterData,
  ) {
    if (!filterProps.customFieldValues?.size) return true;

    const relevantCustomFields = [...filterProps.customFieldValues.entries()].filter(
      ([key]) => customFields.has(key) && customFields.get(key)!.entityType === type,
    );

    if (!relevantCustomFields.length) return null;

    let validDate = true;
    let validBool = true;
    let validListOfValues = true;
    let validListOfTuples = true;
    let validText = true;

    [...filterProps.customFieldValues.entries()].forEach(([key, values]) => {
      const cf = customFields.get(key);
      if (!cf) return false;
      if (cf.entityType !== type) return false;

      if (validDate) validDate = CustomFieldFilter.dateFilter(cf, item, key, values);
      if (validBool) validBool = CustomFieldFilter.boolFilter(cf, item, key, values);
      if (validListOfValues) validListOfValues = CustomFieldFilter.listOfValuesFilter(cf, item, key, values);
      if (validListOfTuples) validListOfTuples = CustomFieldFilter.listOfTuplesFilter(cf, item, key, values);
      if (validText) validText = CustomFieldFilter.textFilter(cf, item, key, values);
    });

    return validDate && validBool && validListOfValues && validText && validListOfTuples;
  }

  static dateFilter(customField: CustomField, item: WithCustomFields, key: string, items?: Set<string>) {
    if (customField.type !== CustomFieldType.date) return true;
    if (!items) return false;

    const dates = items.values();
    const start = dates.next().value;
    const end = dates.next().value;

    if (!start || !end) return false;

    const itemValue = item.customFields.get(key)?.value;
    if (!itemValue) return false;

    const itemDate = new Date(itemValue);
    return itemDate >= new Date(start) && itemDate <= new Date(end);
  }

  static boolFilter(customField: CustomField, item: WithCustomFields, key: string, items?: Set<string>) {
    if (customField.type !== CustomFieldType.bool) return true;
    if (items?.has('false') && !item.customFields.has(key)) return true;
    return items?.has(item.customFields.get(key)?.value || '') || false;
  }

  static textFilter(customField: CustomField, item: WithCustomFields, key: string, items?: Set<string>) {
    if (customField.type !== CustomFieldType.text) return true;
    const cfValue = item.customFields?.get(key)?.value;
    const expectedValues = [...(items?.values() || [])];
    return expectedValues.some(expectedValue => toFilterString(cfValue)?.includes(toFilterString(expectedValue)));
  }

  static listOfValuesFilter(customField: CustomField, item: WithCustomFields, key: string, items?: Set<string>) {
    if (customField.type !== CustomFieldType.listOfValues) return true;
    return items?.has(item.customFields.get(key)?.value || '') || false;
  }

  static listOfTuplesFilter(customField: CustomField, item: WithCustomFields, key: string, items?: Set<string>) {
    if (customField.type !== CustomFieldType.listOfTuples) return true;
    return items?.has(item.customFields.get(key)?.value || '') || false;
  }

  toLabel(): string {
    return asTranslatedString(this.customField.name);
  }

  toIcon(): React.JSX.Element {
    return <></>;
  }

  toDropdownContent(props: BaseFilterProps, open: boolean): React.JSX.Element {
    return <></>;
  }

  toInnerContent(props: BaseFilterProps): string | JSX.Element {
    return <></>;
  }

  toCount(props: BaseFilterProps): number {
    return props.filterProps.customFieldValues?.get(this.customField.id)?.size || 0;
  }

  search(item: CustomField, query: string) {
    return itemsContainText(query, [item.name, item.entityType, item.entitySubtype]);
  }

  filteredItems(items: CustomField[], query: string, filterProps: FilterProps): CustomField[] {
    return items.filter(item => {
      return [CustomFieldFilter.filterItem(filterProps, item), this.search(item, query)].every(
        value => value === null || value,
      );
    });
  }

  toComponent(props: BaseFilterProps): React.JSX.Element {
    return (
      <CustomFieldFilterDropdownContent
        customField={this.customField}
        filter={this}
        props={props}
        defaultElement={super.toComponent(props)}
        customFieldValues={this.values}
      />
    );
  }

  static filterItem(filterProps: FilterProps, item?: CustomField, customFields: Map<string, CustomField> = new Map()) {
    if (item && filterProps.customFieldType?.size && !filterProps.customFieldType.has(item.type)) return false;
    if (item && filterProps.customFieldEntityType?.size && !filterProps.customFieldEntityType.has(item.entityType)) {
      return false;
    }
    if (
      item &&
      filterProps.customFieldEntitySubType?.size &&
      !filterProps.customFieldEntitySubType.has(item.entitySubtype || '')
    ) {
      return false;
    }

    return true;
  }
}
